import { Component, inject } from '@angular/core';
import { RestService } from '@cumlaude/shared-services';
import { DataService } from '../../services/data.service';
import { AccountsService } from '../../services/accounts.service';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { pull } from 'lodash-es';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Dialog } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { CheckboxComponent, InstantSearchBoxComponent } from '@cumlaude/shared-components-inputs';
import { UserService } from '../../services/user.service';
import { DimVsVestiging, RCumLaudeAccount, RVestiging } from '@cumlaude/service-contract';
import { TooltipDirective, TooltipElement } from '@cumlaude/shared-components-overlays';
import { LoadingService, LoadingType } from '../../services/loading.service';
import { saveAs } from 'file-saver';
import { accountLabelMetAfkorting, getTimestamp } from '@cumlaude/shared-utils';
import { ExportColumnDef } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { CssSpinnerComponent } from '../../layout/css-spinner/css-spinner.component';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TableExport } from '../../services/exportable';

interface InstellingVestiging {
	rVestiging: RVestiging;
	vestiging?: DimVsVestiging;
}

@Component({
	selector: 'app-vestigingen',
	templateUrl: './vestigingen.component.html',
	styleUrls: ['./vestigingen.component.scss'],
	imports: [InstantSearchBoxComponent, AsyncPipe, CheckboxComponent, TooltipDirective, CssSpinnerComponent, IconComponent],
})
export class VestigingenComponent {
	protected readonly restService = inject(RestService);
	protected readonly dataService = inject(DataService);
	protected readonly userService = inject(UserService);
	protected readonly accountsService = inject(AccountsService);
	protected readonly dialog = inject(Dialog);
	protected readonly toastr = inject(ToastrService);
	protected readonly loadingService = inject(LoadingService);

	alleVestigingen$: Observable<InstellingVestiging[]>;
	filter$: Observable<string>;
	accounts$: Observable<RCumLaudeAccount[]>;
	showExportSpinner$: Observable<boolean>;

	constructor() {
		this.alleVestigingen$ = combineLatest([this.userService.beheerRVestigingen$, this.dataService.getVestigingen()]).pipe(
			map(([rVestigingen, vestigingen]) => {
				return rVestigingen.map((rVestiging) => {
					const vestiging = vestigingen.find((vestiging) => vestiging.vs_id_vestiging === rVestiging.vestigingId);
					return { rVestiging, vestiging };
				});
			}),
			shareReplay(1)
		);
		this.showExportSpinner$ = this.loadingService.shouldShowLoadingIndicator(LoadingType.EXPORT);
		this.filter$ = this.accountsService.filter$.pipe(map(({ quicksearch }) => quicksearch ?? ''));
		this.accounts$ = this.accountsService.list$;
		this.accountsService.filter$.next({ quicksearch: '' });
	}

	search(quicksearch: string) {
		this.accountsService.filter$.next({ quicksearch });
	}

	toggle(account: RCumLaudeAccount, vestigingId: string, value: boolean) {
		if (account.vestigingen.includes(vestigingId) === value) return;
		if (value) account.vestigingen.push(vestigingId);
		else pull(account.vestigingen, vestigingId);
		this.accountsService.update$.next([account, () => {}, () => {}]);
	}

	getTooltip(vestiging: InstellingVestiging): TooltipElement[] | undefined {
		if (!vestiging.vestiging) return;

		const brins = vestiging.vestiging.vs_is_actief ? vestiging.vestiging.vs_cos_brinvest_actief! : vestiging.vestiging.vs_cos_brinvest!;

		return [
			{
				label: 'Naam:',
				value: vestiging.rVestiging.naam,
			},
			{
				label: 'Vestigingscode(s):',
				value: brins.join(', '),
			},
		];
	}

	async export() {
		const data = await this.generateExportTable();

		try {
			const blob = await firstValueFrom(this.dataService.getTableExport(data));
			saveAs(blob, `CumLaude vestigingen ${getTimestamp()}.xlsx`);
			this.loadingService.stop(LoadingType.EXPORT);
			this.toastr.success('Export succesvol.');
		} catch (error) {
			console.error(error);
			this.toastr.error('Er ging iets mis bij het exporteren. Probeer opnieuw.');
		}
	}

	private async generateExportTable(): Promise<TableExport> {
		const vestigingen = await firstValueFrom(
			this.alleVestigingen$.pipe(map((vestigingen) => vestigingen.map((vestiging) => vestiging.rVestiging)))
		);
		const accounts = await firstValueFrom(this.accounts$);

		const columns: ExportColumnDef[] = [
			{
				name: 'Naam',
				type: 'string',
			},
			{
				name: 'Afkorting',
				type: 'string',
			},
		];
		vestigingen.forEach((vestiging) =>
			columns.push({
				name: vestiging.naam,
				type: 'string',
			})
		);

		const rows: any[][] = accounts.map((account) => {
			const accountVestigingen = vestigingen.map((vestiging) => (account.vestigingen.includes(vestiging.vestigingId) ? 'Ja' : 'Nee'));
			return [account.naam, account.afkorting, ...accountVestigingen];
		});

		return {
			showDatum: true,
			pages: [
				{
					title: 'Beheer > Vestigingen',
					name: 'Vestigingen',
					data: [
						{
							columns: columns,
							rows: rows,
						},
					],
				},
			],
		};
	}

	hoveredRowIndex: number | undefined = undefined;
	hoveredColumnIndex: number | undefined = undefined;

	onHover(rowIndex: number | undefined, columnIndex: number | undefined) {
		this.hoveredRowIndex = rowIndex;
		this.hoveredColumnIndex = columnIndex;
	}

	getAccountNaam(account: RCumLaudeAccount) {
		return accountLabelMetAfkorting(account);
	}
}
